// ─── voicebox web client ─────────────────────────────────────────────────────
// Where the three seams meet:
//   · the SERVICE — one session, one socket, `shared/protocol.ts` and nothing else
//   · the PROVIDER — server-placed audio relays over the socket; client-placed audio
//     stays here and the driver in `driver/` reports what happened up
//   · the FACE — fed `MouthFrame`s tapped off the playback graph, never protocol
//
// This file is only wiring: the audio maths and the socket are `@voicebox/client`,
// the model in `driver/`, the pixels in `face-host.ts`, the chrome in `ui/`.

import { DEFAULT_AUDIO, type AudioFormat, type Placement, type ServerEvent, type TurnTaking } from '@shared/protocol.ts'
import { Microphone, SessionError, SessionLink, Speaker, openSession, type OpenedSession } from '@voicebox/client'
import { MouthDriver, type FaceEmotion, type FaceMode } from '@voicebox/face'
import { BRAND } from './brand.ts'
import { OVERRIDES, PROVIDERS_URL, SESSION_URL } from './config.ts'
import { FaceHost } from './face-host.ts'
import { ToolBridge, createClientDriver, type ClientDriver, type DriverHost } from './driver/index.ts'
import { DEFAULT_SETTINGS, clearModel, loadModel, loadSettings, saveModel, saveSettings, type Settings } from './prefs.ts'
import { CLIENT_TOOL_DECLARATIONS, clientToolHandlers } from './tools.ts'
import { autoPick, deploySecretNames, describeRefusal, fetchCatalog, needs, swapNotice, usable, type ProviderInfo } from './providers.ts'
import { Shell, type Connection, type MicPermission } from './ui/shell.ts'
import { SettingsPanel } from './ui/settings.ts'

class App {
  private settings: Settings
  /** This browser has run the app before, so its saved choices are somebody's. */
  private readonly returning: boolean
  private readonly shell: Shell
  private readonly panel: SettingsPanel
  private readonly face: FaceHost
  private readonly bridge: ToolBridge

  private link: SessionLink | null = null
  private ctx: AudioContext | null = null
  private speaker: Speaker | null = null
  private mouth: MouthDriver | null = null
  private mic: Microphone | null = null
  private driver: ClientDriver | null = null

  private connection: Connection = 'idle'
  private outputFormat: AudioFormat | null = null
  private catalog: ProviderInfo[] = []
  private placement: Placement = 'server'
  private clientTools: string[] = []
  private emotion: FaceEmotion | (string & {}) = 'neutral'
  /** A turn has been committed and nothing has come back yet — the `thinking` face. */
  private awaiting = false
  /** The provider says it is talking. Trusted over the buffer, which a streaming
   * provider empties and refills between frames. */
  private assistantSpeaking = false
  private starting = false

  constructor(host: HTMLElement) {
    const saved = loadSettings()
    this.returning = saved.returning
    this.settings = { ...saved.settings, ...pickOverrides() }

    this.shell = new Shell(host, {
      onTalk: () => (this.link ? this.hangUp('you hung up') : void this.connect()),
      onStop: () => this.bargeIn(),
      onHold: (held) => this.mic?.hold(held),
      onSay: (text) => this.say(text),
      onTurnTaking: (mode) => this.setTurnTaking(mode),
      onOpenSettings: () => this.panel.toggle(),
      onWantAvatar: () => void this.wantAvatar(),
      onDropModel: (file) => void this.wearModel(file),
    })

    this.face = new FaceHost(this.shell.faceStage, (message) => this.shell.notice(message))
    this.panel = new SettingsPanel(document.body, this.settings, {
      onChange: (patch) => this.applySettings(patch),
      onApply: () => void this.reconnect(),
      onPickProvider: (id) => void this.pickProvider(id),
      onPickModel: (file) => void this.pickModel(file),
      onClearModel: () => void this.dropModel(),
    })
    this.bridge = new ToolBridge({
      emit: (ev) => this.link?.send(ev),
      handlers: clientToolHandlers({
        setFace: (id) => this.setFace(id),
        setEmotion: (emotion) => this.setEmotion(emotion),
        currentFace: () => this.face.id,
      }),
      log: (line) => this.shell.transcript.tool(line),
    })

    this.shell.setTurnTaking(this.settings.turnTaking)
    void this.boot()
    this.expose()
  }

  private async boot(): Promise<void> {
    void this.refreshMicPermission()
    await this.setFace(this.settings.face)
    await this.restoreModel()
    void this.loadCatalog()
  }

  /**
   * The provider list is the service's, not ours, so the picker cannot go stale — and
   * until the user pins a choice we follow it. Pasting a key into the Worker is then
   * the whole of "switch to that provider".
   */
  private async loadCatalog(): Promise<void> {
    const catalog = await fetchCatalog(PROVIDERS_URL)
    if (!catalog) {
      this.shell.notice(
        `the service did not list its providers at ${PROVIDERS_URL}`,
        'keeping the saved provider — switching still works',
      )
      return
    }
    this.catalog = catalog
    this.panel.setProviders(catalog)

    if (this.settings.pinned || this.link) return
    const from = this.settings.provider
    const best = autoPick(catalog, from)
    if (best === from) return
    const say = swapNotice(from, best, this.providerInfo(from), this.returning)
    this.applySettings({ provider: best })
    if (say) this.shell.notice(say.head, say.detail)
  }

  private providerInfo(id: string): ProviderInfo | undefined {
    return this.catalog.find((p) => p.id === id)
  }

  /**
   * One click: this provider, from now on, on a fresh session. One that the service
   * says it cannot serve is not switched to silently — the missing secret is named
   * and the working session is left alone.
   */
  private async pickProvider(id: string): Promise<void> {
    const info = this.providerInfo(id)
    if (info && !usable(info)) {
      this.shell.alert(
        info.status === 'planned' ? `${id} is declared but not implemented yet` : `${id} is installed, but ${needs(info)}`,
        info.status === 'planned'
          ? 'pick another provider'
          : [
              `set ${deploySecretNames(info).join(' and ')} as GitHub secrets and re-run deploy-voicebox-server.yml`,
              `locally: ${(info.missingSecrets ?? []).map((s) => `wrangler secret put ${s}`).join(' and ')}`,
            ],
      )
      return
    }
    this.applySettings({ provider: id, pinned: true, placement: '' })
    this.panel.close()
    await this.reconnect()
  }

  // ── microphone permission ──────────────────────────────────────────────────
  /**
   * Shown before anything is asked for, because "why is nothing happening" is almost
   * always a permission the browser is holding.
   */
  private async refreshMicPermission(): Promise<MicPermission> {
    const state = await micPermission()
    this.shell.setMicPermission(state)
    return state
  }

  // ── session lifecycle ──────────────────────────────────────────────────────
  private async connect(): Promise<void> {
    if (this.starting || this.link) return
    this.starting = true
    this.shell.setBusy(true)
    this.setConnection('opening')

    let session: OpenedSession
    try {
      session = await openSession(SESSION_URL, {
        persona: this.settings.persona,
        provider: {
          id: this.settings.provider,
          placement: this.settings.placement || undefined,
          options: this.settings.provider === 'local-pipeline' ? { baseUrl: this.settings.localBaseUrl } : {},
        },
        voice: this.settings.voice || undefined,
        greeting: this.settings.greeting || undefined,
        turnTaking: this.settings.turnTaking,
        tools: CLIENT_TOOL_DECLARATIONS,
        // Left out unless asked for: the service resolves the provider's own native
        // formats, and qwen (16 kHz in) differs from OpenAI (24 kHz).
        audio: OVERRIDES.inputRate
          ? { input: { encoding: 'pcm16', sampleRate: OVERRIDES.inputRate, channels: 1 }, output: DEFAULT_AUDIO }
          : undefined,
        ref: 'voicebox-web',
      })
    } catch (err) {
      const refusal =
        err instanceof SessionError
          ? describeRefusal(err, this.providerInfo(this.settings.provider), SESSION_URL)
          : { head: 'could not open a session', detail: [String(err)] }
      this.shell.alert(refusal.head, refusal.detail)
      this.setConnection('error')
      this.shell.setBusy(false)
      this.starting = false
      return
    }

    this.placement = session.placement
    this.shell.setSession(`${session.provider} · ${session.placement}`, session.placement)
    this.shell.transcript.system(`session ${session.sessionId.slice(0, 8)} · ${session.provider} · ${session.placement}-placed`)

    this.link = new SessionLink(
      session.wsUrl,
      {
        onOpen: () => this.shell.setBusy(false),
        onEvent: (ev) => void this.onEvent(ev, session),
        onAudio: (frame) => this.play(frame),
        onClose: (code, reason) => this.onClose(code, reason),
      },
      DEFAULT_AUDIO,
    )
    this.starting = false
  }

  private async onEvent(ev: ServerEvent, session: OpenedSession): Promise<void> {
    if (this.bridge.accept(ev)) {
      // Consumed as a tool frame; still worth showing, below.
    }
    switch (ev.t) {
      case 'session.ready':
        return this.onReady(ev, session)

      case 'transcript':
        this.awaiting = false
        return this.shell.transcript.push(ev.role, ev.text, ev.final)

      case 'say':
        // The session already recorded it (and broadcast a transcript); a
        // client-placed provider still has to actually speak it.
        return this.driver?.say(ev.text, ev.role)

      case 'speech.start':
        if (ev.role === 'assistant') {
          this.awaiting = false
          this.assistantSpeaking = true
          this.refreshMode()
        }
        return

      case 'speech.stop':
        if (ev.role === 'assistant') {
          this.assistantSpeaking = false
          this.speaker?.endUtterance()
          this.refreshMode()
        }
        return

      case 'cancel':
        return this.bargeIn(false)

      case 'tool.call':
        return this.shell.transcript.tool(`→ ${ev.name}(${short(ev.args)}) · runs at ${ev.runAt}`)

      case 'tool.result':
        this.awaiting = false
        return this.shell.transcript.tool(
          ev.ok ? `← ${ev.name} ${short(ev.result)} · ${ev.ms}ms` : `← ${ev.name} failed: ${ev.error}`,
        )

      case 'audio.format':
        return this.onFormatChange(ev.dir, ev.audio)

      case 'error':
        this.awaiting = false
        this.shell.alert(`${ev.code}: ${ev.message}`, ev.fatal ? 'fatal — the session is over' : undefined)
        if (ev.fatal) this.hangUp(ev.message)
        return

      case 'session.closed':
        return this.hangUp(ev.reason)

      case 'pong':
        return
    }
  }

  private async onReady(ready: Extract<ServerEvent, { t: 'session.ready' }>, session: OpenedSession): Promise<void> {
    this.clientTools = ready.clientTools
    this.placement = ready.placement
    this.shell.setTurnTaking(ready.turnTaking)

    try {
      const ctx = await this.audioContext(ready.audio.output)
      this.outputFormat = ready.audio.output
      this.speaker = new Speaker(ctx, ready.audio.output)
      // The face is driven from the playback graph itself, so lipsync is right
      // whatever produced the audio — a cloud provider or a local TTS.
      this.mouth = new MouthDriver(this.speaker.tap)
      this.mouth.start((frame) => {
        this.face.drive(frame)
        this.refreshMode()
      })

      if (ready.placement === 'client') {
        this.driver = createClientDriver(ready.clientConfig ?? { provider: ready.provider }, this.driverHost(), {
          greeting: this.settings.greeting || undefined,
        })
      }

      this.mic = await Microphone.open(ctx, ready.audio.input, {
        onFrame: (pcm) => this.onMicFrame(pcm),
        onLevel: (level) => this.shell.setLevel(level),
        onEdge: (edge) => this.onMicEdge(edge),
      }, { gate: ready.turnTaking })
      void this.refreshMicPermission()

      this.setConnection('live')
      this.refreshMode()
      await this.driver?.start()
    } catch (err) {
      this.shell.alert('could not start audio', explain(err))
      void this.refreshMicPermission()
      this.setConnection('error')
      this.hangUp('audio unavailable')
      return
    }
    void session
  }

  private onClose(code: number, reason: string): void {
    // 1006 is the browser's "no idea": a refused token or a blocked Origin lands here.
    this.shell.alert(
      code === 1006 ? 'the session socket closed before it opened' : `session socket closed (${code})`,
      reason || 'a refused token, an Origin the service does not allow, or the service being down',
    )
    this.hangUp(reason || `closed (${code})`)
  }

  private hangUp(reason: string): void {
    if (!this.link && !this.mic) return
    this.mic?.close()
    this.mouth?.stop()
    this.speaker?.dispose()
    this.driver?.close()
    this.link?.close()
    this.mic = null
    this.mouth = null
    this.speaker = null
    this.driver = null
    this.link = null
    this.awaiting = false
    this.assistantSpeaking = false
    this.clientTools = []
    this.shell.setBusy(false)
    this.setConnection('idle')
    this.shell.setSession('no session')
    this.shell.setLevel(0)
    this.shell.transcript.system(`session ended — ${reason}`)
    this.refreshMode()
    this.face.drive({ t: 0, level: 0 }) // close the mouth
  }

  /**
   * One connection state, shown and reported. It only reaches `live` once audio is
   * actually running — a UI (or a harness) that trusted the raw socket would claim
   * the session was usable while the microphone was still opening.
   */
  private setConnection(connection: Connection): void {
    this.connection = connection
    this.shell.setConnection(connection)
  }

  private async reconnect(): Promise<void> {
    if (this.link) this.hangUp('restarting with new settings')
    await this.connect()
  }

  // ── audio ──────────────────────────────────────────────────────────────────
  private async audioContext(output: AudioFormat): Promise<AudioContext> {
    if (!this.ctx || this.ctx.state === 'closed') {
      // Matching the output rate avoids a resample on the hot path. If the device
      // refuses the hint, AudioBuffers carry their own rate and still play correctly.
      this.ctx = new AudioContext({ sampleRate: output.sampleRate, latencyHint: 'interactive' })
    }
    if (this.ctx.state === 'suspended') await this.ctx.resume()
    return this.ctx
  }

  private play(frame: ArrayBuffer): void {
    this.awaiting = false
    try {
      this.speaker?.enqueue(frame)
    } catch (err) {
      this.shell.alert('cannot play this audio', explain(err))
    }
  }

  private onMicFrame(pcm: ArrayBuffer): void {
    // Client placement: the audio must not leave the machine, so it goes to the
    // local driver instead of up the socket. That is the whole difference.
    if (this.placement === 'client') this.driver?.pushAudio(pcm)
    else this.link?.sendAudio(pcm)
  }

  private onMicEdge(edge: 'start' | 'stop'): void {
    if (edge === 'start') {
      // Local barge-in, without waiting for the server to echo `cancel` back.
      this.bargeIn(false)
      this.link?.send({ t: 'speech.start' })
      this.refreshMode()
      return
    }
    this.link?.send({ t: 'speech.stop' })
    if (this.settings.turnTaking === 'ptt') this.link?.send({ t: 'audio.commit' })
    // A client-placed provider gets no help from the session here: the DO's relay
    // has nothing to commit, so the local driver runs its own turn.
    this.driver?.commit()
    this.awaiting = true
    this.refreshMode()
  }

  /** Drop buffered audio now. `announce` tells the session; a server `cancel` does not. */
  private bargeIn(announce = true): void {
    this.speaker?.cancel()
    this.driver?.cancel()
    this.assistantSpeaking = false
    this.awaiting = false
    if (announce) this.link?.send({ t: 'cancel' })
    this.refreshMode()
  }

  private onFormatChange(dir: 'input' | 'output', audio: AudioFormat): void {
    if (dir === 'output') return this.speaker?.setFormat(audio)
    this.shell.notice(`the session changed the input format to ${audio.encoding}/${audio.sampleRate}Hz`, 'reopening the microphone')
    void this.reopenMic(audio)
  }

  private async reopenMic(audio: AudioFormat): Promise<void> {
    const ctx = this.ctx
    if (!ctx || !this.mic) return
    this.mic.close()
    this.mic = await Microphone.open(ctx, audio, {
      onFrame: (pcm) => this.onMicFrame(pcm),
      onLevel: (level) => this.shell.setLevel(level),
      onEdge: (edge) => this.onMicEdge(edge),
    }, { gate: this.settings.turnTaking })
  }

  // ── the face ───────────────────────────────────────────────────────────────
  /**
   * One derivation of `FaceMode`, used for both the face and the status pill, so
   * they cannot disagree about what is happening.
   */
  private refreshMode(): void {
    const mode: FaceMode = !this.link
      ? 'idle'
      : this.assistantSpeaking || this.speaker?.speaking
        ? 'speaking'
        : this.awaiting
          ? 'thinking'
          : 'listening'
    this.shell.setMode(mode)
    this.face.setState({ mode, emotion: this.emotion })
  }

  private async setFace(id: string): Promise<void> {
    await this.face.use(id)
    this.applySettings({ face: this.face.id })
    this.refreshMode()
    this.panel.setModelState({ takesModel: this.face.takesModel, hasModel: this.face.hasModel, name: this.settings.modelName })
  }

  private setEmotion(emotion: string): void {
    this.emotion = emotion
    this.refreshMode()
  }

  private async restoreModel(): Promise<void> {
    if (!this.face.takesModel) return
    try {
      const saved = await loadModel()
      if (saved) await this.face.loadModel(saved)
    } catch (err) {
      this.shell.notice('the saved avatar could not be reloaded', explain(err))
    }
    this.panel.setModelState({ takesModel: this.face.takesModel, hasModel: this.face.hasModel, name: this.settings.modelName })
  }

  /** The first-run guide's avatar button: mount the face that takes one, then ask. */
  private async wantAvatar(): Promise<void> {
    if (!this.face.takesModel) await this.setFace('vrm')
    this.panel.openAt('model')
  }

  /** A dropped `.vrm` or `.mavatar`: switch to the face that can wear it and wear it. */
  private async wearModel(file: File): Promise<void> {
    const face = /\.vrm$/i.test(file.name) ? 'vrm' : /\.mavatar$/i.test(file.name) ? 'mesh' : /\.layered\.json$/i.test(file.name) ? 'layered' : null
    if (!face) {
      return this.shell.notice(`${file.name} is not a .vrm, .mavatar or .layered.json`, 'export a .vrm from VRoid Studio or a .mavatar from Mesh Avatar Studio')
    }
    if (this.face.id !== face) await this.setFace(face)
    await this.pickModel(file)
  }

  private async pickModel(file: File): Promise<void> {
    try {
      await this.face.loadModel(file)
      await saveModel(file)
      this.applySettings({ modelName: file.name })
    } catch (err) {
      this.shell.alert(`could not load ${file.name}`, explain(err))
    }
    this.panel.setModelState({ takesModel: this.face.takesModel, hasModel: this.face.hasModel, name: this.settings.modelName })
  }

  private async dropModel(): Promise<void> {
    await clearModel().catch(() => {})
    this.face.clearModel()
    this.applySettings({ modelName: '' })
    await this.face.use(this.face.id) // remount, back to the stand-in
    this.panel.setModelState({ takesModel: this.face.takesModel, hasModel: false, name: '' })
  }

  // ── settings ───────────────────────────────────────────────────────────────
  private applySettings(patch: Partial<Settings>): void {
    this.settings = { ...this.settings, ...patch }
    saveSettings(this.settings)
    if (patch.turnTaking) this.setTurnTaking(patch.turnTaking)
    if (patch.face && patch.face !== this.face.id) void this.setFace(patch.face)
    this.panel.setValues(this.settings)
  }

  private setTurnTaking(mode: TurnTaking): void {
    this.settings.turnTaking = mode
    saveSettings(this.settings)
    this.shell.setTurnTaking(mode)
    this.mic?.setGate(mode)
    this.refreshMode()
  }

  private say(text: string): void {
    if (!this.link) return this.shell.alert('not connected', 'press Start talking first')
    // Not rendered here: the session records a `text` turn and broadcasts it back, and
    // it is the only thing allowed to own the transcript. Rendering both duplicates it.
    this.link.send({ t: 'text', text })
    this.awaiting = true
    this.refreshMode()
  }

  private driverHost(): DriverHost {
    return {
      emit: (ev) => this.link?.send(ev),
      play: (audio) => {
        // A client driver hands over a whole synthesised utterance at once.
        this.play(audio)
        this.speaker?.endUtterance()
      },
      runTool: (name, args) => this.bridge.run(name, args, this.clientTools),
      tools: () =>
        CLIENT_TOOL_DECLARATIONS.filter((t) => t.description && t.parameters).map((t) => ({
          name: t.name,
          description: t.description!,
          parameters: t.parameters as Record<string, unknown>,
        })),
      fetch: (input, init) => fetch(input, init),
      fail: (message) => this.shell.alert(message),
      log: (line) => this.shell.transcript.tool(line),
    }
  }

  /**
   * A small observable surface: what the Playwright harnesses assert against, and
   * what makes a misbehaving audio path debuggable from a console.
   */
  private expose(): void {
    Object.assign(globalThis, {
      __vb: {
        stats: () => ({
          connection: this.connection,
          socket: this.link?.connected ?? false,
          mode: this.face.mode,
          provider: this.settings.provider,
          placement: this.link ? this.placement : null,
          face: this.face.id,
          clientTools: this.clientTools,
          micBytes: this.mic?.sent ?? 0,
          micOpen: this.mic?.open ?? false,
          micBlocks: this.mic?.blocks ?? 0,
          // The mic encodes to whatever THIS session declared (qwen listens at 16 kHz,
          // OpenAI at 24), so switching provider must never need a reload.
          micRate: this.mic?.targetRate ?? null,
          deviceRate: this.mic?.deviceRate ?? null,
          outputRate: this.outputFormat?.sampleRate ?? null,
          turnTaking: this.settings.turnTaking,
          micGate: this.mic?.gateMode ?? null,
          audio: this.ctx ? { state: this.ctx.state, sampleRate: this.ctx.sampleRate } : null,
          sent: this.link?.bytesSent ?? 0,
          received: this.link?.bytesReceived ?? 0,
          speaker: this.speaker?.stats() ?? null,
          // Bounded: a harness polls this, and one long turn should not make each
          // poll megabytes of text.
          transcript: this.shell.transcript.text.slice(-6000),
        }),
        connect: () => this.connect(),
        hangUp: () => this.hangUp('asked to'),
        say: (text: string) => this.say(text),
        hold: (held: boolean) => this.mic?.hold(held),
        bargeIn: () => this.bargeIn(),
        face: (id: string) => this.setFace(id),
        provider: (id: string) => this.pickProvider(id),
        avatar: () => this.wantAvatar(),
        modelState: () => ({ takesModel: this.face.takesModel, hasModel: this.face.hasModel, name: this.settings.modelName }),
        catalog: () => this.catalog,
        settings: (patch: Partial<Settings>) => this.applySettings(patch),
      },
    })
  }
}

// ── helpers ──────────────────────────────────────────────────────────────────
function pickOverrides(): Partial<Settings> {
  const patch: Partial<Settings> = {}
  // A provider named in the URL is an explicit choice, so it pins: the catalog must
  // not move a harness (or a shared link) onto something else.
  if (OVERRIDES.provider) {
    patch.provider = OVERRIDES.provider
    patch.pinned = true
  }
  if (OVERRIDES.placement === 'server' || OVERRIDES.placement === 'client') patch.placement = OVERRIDES.placement
  if (OVERRIDES.face) patch.face = OVERRIDES.face
  if (OVERRIDES.turnTaking === 'vad' || OVERRIDES.turnTaking === 'ptt') patch.turnTaking = OVERRIDES.turnTaking
  return patch
}

function explain(err: unknown): string {
  if (err instanceof Error && err.name === 'NotAllowedError') return 'microphone permission was refused'
  if (err instanceof Error && err.name === 'NotFoundError') return 'no microphone was found'
  return err instanceof Error ? err.message : String(err)
}

/** Chrome and friends can tell us before we ask; Safari cannot, and says so. */
async function micPermission(): Promise<MicPermission> {
  try {
    const status = await navigator.permissions.query({ name: 'microphone' as PermissionName })
    return status.state as MicPermission
  } catch {
    return 'unknown'
  }
}

function short(value: unknown): string {
  const text = typeof value === 'string' ? value : JSON.stringify(value ?? null)
  return text.length > 90 ? `${text.slice(0, 90)}…` : text
}

document.title = `${BRAND.name} — ${BRAND.tagline}`
new App(document.getElementById('app')!)
document.getElementById('boot')?.remove()

// Defaults are exported for the harnesses, which drive a pristine browser profile.
export { DEFAULT_SETTINGS }
